import { Observable } from 'rxjs';

import { TpvReportFile, TpvReportFormat } from '@app/domain/models/tpv/tpv-report.model';
import { TransactionListQuery } from './transaction.repository';

/**
 * Filtros del reporte de comercios (contrato backend §2.2). Mismas keys que el
 * listado `GET /merchants/`: multi-valor en CSV (OR intra-filtro, AND entre
 * filtros); ausente = "todos". El backend acota al scope del usuario.
 */
export interface MerchantReportFilters {
  /** Nombre del comercio (prefijo). */
  merchantName?: string;
  /** Razón social (prefijo). */
  socialName?: string;
  /** Empresa (prefijo). */
  companyName?: string;
  /** `MORAL` / `FISICA`. */
  entity?: string[];
  /** `ACTIVO` / `INACTIVO` / `PAUSADO` / `ELIMINADO`. */
  status?: string[];
  /** Comercios de una unidad concreta. */
  idUnit?: string | number;
  /** PK entero de la Company org → comercios de esa empresa (intersectado con scope). */
  orgIdCompany?: number;
  /** UUIDs de comercios a incluir. */
  idMerchant?: string[];
  /** Atajo legacy: `true` = `status=ELIMINADO`. */
  deleted?: boolean;
}

/**
 * Filtros del reporte de empresas (contrato backend §2.3). Mismas keys que
 * `GET /companies/`.
 */
export interface CompanyReportFilters {
  /** Nombre comercial (prefijo). */
  name?: string;
  /** Razón social (prefijo). */
  socialName?: string;
  /** `MORAL` / `FISICA`. */
  entity?: string[];
  /** `ACTIVO` / `INACTIVO` / `ELIMINADO`. */
  status?: string[];
  /** UUIDs de empresa (intersectado con scope). */
  idCompany?: string[];
  /** Atajo legacy: `true` = `status=ELIMINADO`. */
  deleted?: boolean;
}

/**
 * Filtros del reporte de unidades de negocio (contrato backend §2.4). Mismas keys
 * que `GET /business-units/`.
 */
export interface BusinessUnitReportFilters {
  /** Nombre de la unidad (prefijo). */
  name?: string;
  /** Empresa (prefijo). */
  companyName?: string;
  /** Razón social (prefijo). */
  socialName?: string;
  /** `MORAL` / `FISICA`. */
  entity?: string[];
  /** `ACTIVO` / `INACTIVO` / `ELIMINADO`. */
  status?: string[];
  /** UUIDs de unidad (intersectado con scope). */
  idUnit?: string[];
  /** Unidades de una empresa concreta (contexto). */
  idCompany?: string | number;
  /** Atajo legacy: `true` = `status=ELIMINADO`. */
  deleted?: boolean;
}

/** Filtros del reporte de usuarios global (`GET /users/report/`, admin). */
export interface UserReportFilters {
  /** Búsqueda por nombre o email. */
  search?: string;
  /** Rol: `OPERADOR` / `GESTION`. */
  roleName?: string;
  /** `ACTIVO` / `INACTIVO` / `PAUSADO`. */
  status?: string[];
}

/** Filtros del reporte de operadores de una empresa (`GET …/operators/report/`). */
export interface OperatorReportFilters {
  search?: string;
  roleName?: string;
  status?: string[];
  /** `true` = solo operadores company-wide. */
  companyWide?: boolean;
}

/** Filtros del reporte de dispersiones (`GET /dispersions/report/`). */
export interface DispersionReportFilters {
  /** UUID del comercio (opcional). */
  idMerchant?: string;
  merchantName?: string;
  companyName?: string;
  unitName?: string;
  /** Procesadores (CSV): `AMEX` / `BANORTE` / `BANCO`. */
  processors?: string[];
  /** Estado: `En Proceso` / `Pagada` / … */
  status?: string;
  /** Días transcurridos. */
  days?: number;
  minAmount?: number;
  maxAmount?: number;
  /** `YYYY-MM-DD`. */
  startDate?: string;
  endDate?: string;
}

/**
 * Variantes de reporte de dispersiones proxeadas por suite:
 * `aprobadas` (Transacciones Aprobadas), `acumulado` (Dispersiones acumulado)
 * y `detalle` (Detalle de Transacciones). Cada una vive en
 * `GET /dispersions/report/<variante>/`.
 */
export type DispersionReportVariant = 'aprobadas' | 'acumulado' | 'detalle';

/**
 * Filtros de las variantes de reporte de dispersiones. OJO: el rango es de
 * FECHA A LIQUIDAR (T+1 Nac/Int, T+3 AMEX), no de fecha de operación. Sin
 * `idMerchant`, suite acota a los comercios del scope del usuario. Sin fechas,
 * el backend usa hoy (MX).
 */
export interface DispersionVariantReportFilters {
  /** UUID del comercio (opcional → scope del usuario). */
  idMerchant?: string;
  /** Inicio del rango de fecha a liquidar, `YYYY-MM-DD`. */
  settlementStart?: string;
  /** Fin del rango; el backend usa `= settlementStart` si se omite. */
  settlementEnd?: string;
  /** Acota a una empresa (nombre). */
  companyName?: string;
  /** Acota a una unidad (nombre). */
  unitName?: string;
  /**
   * Solo `acumulado`: controla qué columnas de comisión aparecen
   * (EMPRESA | UNIDAD | COMERCIO). Omitido → el backend lo deriva del scope.
   */
  level?: 'EMPRESA' | 'UNIDAD' | 'COMERCIO';
  /**
   * Estados del listado (`APROBADA`, `DEVUELTA`, …). Historia de usuario
   * Transacciones Aprobadas (2026-09-30): la UI cablea los 9 filtros del panel a
   * la descarga aunque el backend HOY los ignore — el listado sí filtra por ellos y
   * el archivo debe espejar lo que el operador ve. Multi-valor CSV vía `csv(...)`.
   */
  status?: string[];
  /**
   * Medio de pago (`CREDITO`, `DEBITO`, …). Historia de usuario 2026-09-30 —
   * backend hoy lo ignora, no rompe. Multi-valor CSV.
   */
  paymentMethod?: string[];
  /**
   * Marca de tarjeta (`VISA`, `MASTERCARD`, …). Historia de usuario 2026-09-30 —
   * backend hoy lo ignora, no rompe. Multi-valor CSV.
   */
  cardBrand?: string[];
  /**
   * Tipo de operación (`VENTA`, `CHECK_IN`, …). Historia de usuario 2026-09-30 —
   * backend hoy lo ignora, no rompe. Multi-valor CSV.
   */
  operationType?: string[];
  /**
   * Procesador (`PROSA`, `AMERICAN_EXPRESS`, …). Historia de usuario 2026-09-30 —
   * backend hoy lo ignora, no rompe. Multi-valor CSV.
   */
  processor?: string[];
  /**
   * Identificadores de terminal. Historia de usuario 2026-09-30 — backend hoy lo
   * ignora, no rompe. Multi-valor CSV.
   */
  idTerminal?: string[];
  /**
   * Correo del cliente. Historia de usuario 2026-09-30 — backend hoy lo ignora,
   * no rompe. UN solo valor (no CSV), consistente con el filtro del listado.
   */
  email?: string;
  /**
   * `gross_amount >= minAmount`. Historia de usuario 2026-09-30 — backend hoy lo
   * ignora, no rompe.
   */
  minAmount?: number;
  /**
   * `gross_amount <= maxAmount`. Historia de usuario 2026-09-30 — backend hoy lo
   * ignora, no rompe.
   */
  maxAmount?: number;
}

/** Filtros del reporte de liquidaciones (`GET /settlements/report/`). */
export interface SettlementReportFilters {
  idMerchant?: string;
  merchantName?: string;
  companyName?: string;
  unitName?: string;
  idDispersion?: string | number;
  bank?: string;
  trackingKey?: string;
  minAmount?: number;
  maxAmount?: number;
  /** ISO datetime. */
  startDate?: string;
  endDate?: string;
}

/** Filtros del reporte de terminales (`GET /terminals/report/`). */
export interface TerminalReportFilters {
  /** UUID del comercio (opcional → scope). */
  idMerchant?: string;
  idTerminal?: string;
  serialNumber?: string;
  name?: string;
  merchantName?: string;
  companyName?: string;
  unitName?: string;
  /** `Conectada` / `Desconectada`. */
  status?: string;
  /** `Actualizada` / `Desactualizada` / `No instalada`. */
  versionApp?: string;
  idAddress?: string;
}

/**
 * Reportes descargables de TPV (contrato §1–§2). El backend genera el archivo en
 * Base64 acotado al scope del usuario y respondiendo EXACTAMENTE a los filtros
 * enviados (los mismos del listado). Cada método devuelve el archivo ya mapeado.
 */
export abstract class TpvReportRepository {
  /**
   * Reporte de transacciones (caso especial §2.1): responde a todos los filtros
   * activos + rango de fechas. Sin `externalIdMerchant` → todos los comercios del
   * usuario. `query` reutiliza los mismos campos del listado de transacciones.
   */
  abstract getTransactionsReport(
    format: TpvReportFormat,
    externalIdMerchant: string | undefined,
    query: TransactionListQuery,
    /** `true` = reporte CON desglose (`?breakdown=true`). EXIGE `externalIdMerchant`. */
    breakdown?: boolean,
  ): Observable<TpvReportFile>;

  abstract getMerchantsReport(
    format: TpvReportFormat,
    filters: MerchantReportFilters,
  ): Observable<TpvReportFile>;

  abstract getCompaniesReport(
    format: TpvReportFormat,
    filters: CompanyReportFilters,
  ): Observable<TpvReportFile>;

  abstract getBusinessUnitsReport(
    format: TpvReportFormat,
    filters: BusinessUnitReportFilters,
  ): Observable<TpvReportFile>;

  /** Reporte de usuarios global (admin) — `GET /users/report/`. */
  abstract getUsersReport(
    format: TpvReportFormat,
    filters: UserReportFilters,
  ): Observable<TpvReportFile>;

  /** Reporte de operadores de una empresa — `GET /companies/<id_company>/operators/report/`. */
  abstract getOperatorsReport(
    format: TpvReportFormat,
    idCompany: number | string,
    filters: OperatorReportFilters,
  ): Observable<TpvReportFile>;

  /** Reporte de dispersiones — `GET /dispersions/report/`. */
  abstract getDispersionsReport(
    format: TpvReportFormat,
    filters: DispersionReportFilters,
  ): Observable<TpvReportFile>;

  /**
   * Variantes de reporte de dispersiones — `GET /dispersions/report/<variante>/`
   * (aprobadas | acumulado | detalle). Responden al rango de fechas del listado.
   */
  abstract getDispersionsVariantReport(
    variant: DispersionReportVariant,
    format: TpvReportFormat,
    filters: DispersionVariantReportFilters,
  ): Observable<TpvReportFile>;

  /** Reporte de liquidaciones — `GET /settlements/report/`. */
  abstract getSettlementsReport(
    format: TpvReportFormat,
    filters: SettlementReportFilters,
  ): Observable<TpvReportFile>;

  /** Reporte de terminales — `GET /terminals/report/`. */
  abstract getTerminalsReport(
    format: TpvReportFormat,
    filters: TerminalReportFilters,
  ): Observable<TpvReportFile>;
}
